import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useOAuthConnect } from '../../hooks/useOAuthConnect';

/**
 * Onboarding modal shown to users who haven't linked any social accounts.
 * Provides inline connect buttons for LinkedIn and X — no redirect needed.
 */
const OnboardingModal = ({ isOpen, onSkip }) => {
  const navigate = useNavigate();
  const { connect, loading, isLinkedInConnected, isTwitterConnected } = useOAuthConnect();

  if (!isOpen) return null;

  const allConnected = isLinkedInConnected && isTwitterConnected;

  const handleDone = () => {
    onSkip();
    navigate('/idea-refiner', { state: { newSession: true } });
  };

  const platforms = [
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: 'Linkedin',
      color: '#0A66C2',
      description: 'Publish professional posts',
      connected: isLinkedInConnected,
    },
    {
      id: 'twitter',
      name: 'X',
      icon: 'X',
      color: '#A78BFA',
      description: 'Share with your audience',
      connected: isTwitterConnected,
    },
  ];

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      <div className="relative w-full max-w-md mx-4 bg-background border border-border rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-8 pt-8 pb-2 text-center">
          <div className="flex justify-center gap-3 mb-5">
            {platforms.map(p => (
              <div key={p.id} className={`w-11 h-11 rounded-xl flex items-center justify-center ${p.connected ? 'bg-emerald-500/10' : 'bg-muted'}`}
                style={!p.connected ? { backgroundColor: `${p.color}10` } : {}}>
                {p.connected ? (
                  <Icon name="CheckCircle" size={22} className="text-emerald-500" />
                ) : (
                  <Icon name={p.icon} size={22} color={p.color} />
                )}
              </div>
            ))}
          </div>

          <h2 className="text-xl font-semibold text-foreground mb-2">
            {allConnected ? 'You\'re all set!' : 'Connect your accounts'}
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {allConnected
              ? 'Both accounts connected. You\'re ready to create and publish posts.'
              : 'Link your social accounts to publish posts directly from POSTRequest.'
            }
          </p>
        </div>

        {/* Platform connect buttons */}
        <div className="px-8 py-5 space-y-3">
          {platforms.map(p => (
            <div key={p.id} className="flex items-center gap-3 px-4 py-3 bg-card border border-border rounded-xl">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${p.color}15` }}>
                <Icon name={p.icon} size={18} color={p.color} />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-foreground">{p.name}</span>
                <p className="text-xs text-muted-foreground">{p.description}</p>
              </div>
              {p.connected ? (
                <span className="flex items-center gap-1 px-2.5 py-1 bg-emerald-500/10 text-emerald-400 text-xs font-medium rounded-lg">
                  <Icon name="Check" size={12} /> Connected
                </span>
              ) : (
                <button
                  onClick={() => connect(p.id)}
                  disabled={loading?.[p.id]}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-lg hover:opacity-90 transition-all disabled:opacity-50"
                  style={{ background: `linear-gradient(135deg, ${p.color}, ${p.color}CC)` }}
                >
                  {loading?.[p.id] ? (
                    <><Icon name="Loader" size={12} className="animate-spin" /> Connecting...</>
                  ) : (
                    <><Icon name="Link" size={12} /> Connect</>
                  )}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="px-8 pb-8 space-y-2">
          <button
            onClick={handleDone}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold rounded-xl transition-all ${
              allConnected
                ? 'bg-gradient-to-r from-[#7E6BC4] to-[#A78BFA] text-white hover:opacity-90'
                : 'bg-muted hover:bg-muted text-foreground'
            }`}
          >
            {allConnected ? (
              <><Icon name="ArrowRight" size={16} /> Start Creating</>
            ) : (
              'Skip for now — I\'ll connect later'
            )}
          </button>
          <p className="text-[10px] text-muted-foreground text-center">
            You can connect accounts anytime from Link Accounts in the sidebar
          </p>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
